import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, Outlet, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { rupees, formatIndian, purityColor, formatApiError, formatWeight } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, LogOut, Package, FileSpreadsheet, Info, Gem } from "lucide-react";
import ProductDetailDrawer from "@/components/ProductDetailDrawer";

function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/login"); };

  const nav = [
    { to: "/portal", end: true, icon: Package, label: "Products", testid: "portal-nav-products" },
    { to: "/portal/search", icon: Search, label: "Search", testid: "portal-nav-search" },
  ];
  if (user?.role === "franchise_owner") {
    nav.push({ to: "/portal/reports", icon: FileSpreadsheet, label: "Reports", testid: "portal-nav-reports" });
  }

  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      <header className="glass-nav sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-serif text-2xl tracking-[0.3em] text-[#0D1B2A]">APS</span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-slate-500">Ashth Pricing</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden md:block">
              <div className="text-sm text-[#0D1B2A] font-medium">{user?.franchise_name || user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#C9A84C]">{user?.role === "franchise_owner" ? "Owner" : "Team"}</div>
            </div>
            <button onClick={doLogout} className="flex items-center gap-1 text-xs text-slate-500 hover:text-[#0D1B2A]" data-testid="portal-logout">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex gap-6 border-t border-[#0D1B2A]/5">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
              className={({ isActive }) => `py-3 text-sm flex items-center gap-2 border-b-2 ${isActive ? "border-[#C9A84C] text-[#0D1B2A]" : "border-transparent text-slate-500"}`}>
              <n.icon className="w-4 h-4" strokeWidth={1.5} /> {n.label}
            </NavLink>
          ))}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10"><Outlet /></main>
    </div>
  );
}

function CatalogueView() {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    const t = setTimeout(() => {
      const params = search ? { search } : {};
      api.get("/products", { params }).then((r) => setItems(r.data)).catch(() => setItems([]));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Catalogue</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">{user?.franchise_name}</h1>
        <p className="text-slate-500 mt-2">Your products and pricing.</p>
      </div>

      <div className="luxe-card p-2 flex items-center gap-2 max-w-2xl">
        <Search className="w-5 h-5 text-slate-400 ml-3" strokeWidth={1.5} />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search your catalogue…"
          className="border-0 shadow-none focus-visible:ring-0 h-12 bg-transparent" data-testid="portal-search-input" />
      </div>

      {items === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="skeleton h-72" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="luxe-card p-16 text-center">
          <Gem className="w-10 h-10 mx-auto text-[#C9A84C] mb-3" strokeWidth={1} />
          <h3 className="font-serif text-2xl text-[#0D1B2A]">No products in your catalogue</h3>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map((it) => <PortalCard key={it.product.jewel_code} item={it} role={user?.role} onClick={() => setSelected(it.product.jewel_code)} />)}
        </div>
      )}

      {selected && <ProductDetailDrawer jewelCode={selected} onClose={() => setSelected(null)} level={user?.role === "franchise_owner" ? "franchise_owner" : "franchise_team"} />}
    </div>
  );
}

function SearchView() {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const { user } = useAuth();

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/products/${encodeURIComponent(query.trim())}`);
      setResult({ data, error: null });
    } catch (e) {
      if (e.response?.status === 404) {
        setResult({ data: null, error: "Product not found — please check the Jewel Code and try again." });
      } else {
        setResult({ data: null, error: formatApiError(e.response?.data?.detail) || "Search failed" });
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Lookup</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">Search any product</h1>
        <p className="text-slate-500 mt-2">Enter any Jewel Code from the master catalogue.</p>
      </div>

      <form onSubmit={doSearch} className="luxe-card p-2 flex items-center gap-2 max-w-2xl">
        <Search className="w-5 h-5 text-slate-400 ml-3" strokeWidth={1.5} />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Jewel Code"
          className="border-0 shadow-none focus-visible:ring-0 h-12 bg-transparent" data-testid="portal-cross-search-input" />
        <Button type="submit" disabled={busy} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white h-11" data-testid="portal-cross-search-btn">Lookup</Button>
      </form>

      {result && result.error && (
        <div className="luxe-card p-6 border-l-4 border-red-400">
          <p className="text-sm text-red-700">{result.error}</p>
        </div>
      )}

      {result && result.data && (
        <div className="space-y-4">
          {!result.data.in_catalogue && (
            <div className="luxe-card p-4 border-l-4 border-[#C9A84C] bg-[#C9A84C]/5 flex items-start gap-3" data-testid="not-in-catalogue-banner">
              <Info className="w-5 h-5 text-[#C9A84C] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div className="text-sm">
                <span className="font-medium text-[#0D1B2A]">This product is not in your current catalogue.</span>
                <span className="text-slate-600"> Showing pricing based on your access level.</span>
              </div>
            </div>
          )}
          <div className="luxe-card p-6">
            <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-1">Result</p>
            <h3 className="font-serif text-2xl text-[#0D1B2A]">{result.data.product.jewel_code}</h3>
            <p className="text-sm text-slate-500 mt-1">{result.data.product.style_no} · {result.data.product.category}</p>
            <Button onClick={() => setSelected(result.data.product.jewel_code)} className="mt-4 bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="open-search-result-btn">
              View full detail
            </Button>
          </div>
        </div>
      )}

      {selected && <ProductDetailDrawer jewelCode={selected} onClose={() => setSelected(null)} level={user?.role === "franchise_owner" ? "franchise_owner" : "franchise_team"} />}
    </div>
  );
}

function ReportsView() {
  const { user } = useAuth();
  const downloadReport = () => {
    if (!user?.franchise_id) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/franchise/${user.franchise_id}/excel`;
    const tok = localStorage.getItem("access_token");
    fetch(url, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} })
      .then((r) => r.blob()).then((b) => {
        const u = URL.createObjectURL(b); const a = document.createElement("a");
        a.href = u; a.download = `${user.franchise_name}_report.xlsx`; a.click();
        URL.revokeObjectURL(u);
      });
  };
  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Exports</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">Reports</h1>
      </div>
      <div className="luxe-card p-8 max-w-2xl">
        <p className="text-sm text-slate-500 mb-4">Download your franchise catalogue with pricing.</p>
        <Button onClick={downloadReport} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="portal-report-btn">
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Download Excel
        </Button>
      </div>
    </div>
  );
}

function PortalCard({ item, role, onClick }) {
  const p = item.product;
  const pr = item.pricing;
  return (
    <button onClick={onClick} data-testid={`portal-card-${p.jewel_code}`} className="luxe-card text-left p-0 overflow-hidden group">
      <div className="aspect-square bg-[#FAF8F3] flex items-center justify-center overflow-hidden">
        {p.photo_url ? <img src={p.photo_url} alt={p.jewel_code} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          : <span className="font-serif text-5xl tracking-[0.25em] text-[#C9A84C]/30">APS</span>}
      </div>
      <div className="p-5 space-y-3">
        <div className="flex justify-between items-start">
          <div>
            <div className="font-medium text-[#0D1B2A]">{p.jewel_code}</div>
            <div className="text-xs text-slate-500 mt-0.5">{p.style_no}</div>
          </div>
          <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${purityColor(p.purity)}`}>{p.purity}</span>
        </div>
        <div className="flex justify-between text-xs text-slate-500">
          <span>{p.category}</span><span className="tabular-nums">{formatWeight(p.net_weight)} g</span>
        </div>
        <div className="pt-3 border-t border-[#0D1B2A]/5">
          {role === "franchise_owner" && pr.franchise && (
            <div className="flex justify-between text-xs mb-1.5">
              <span className="text-slate-500">Your Cost</span>
              <span className="font-semibold tabular-nums text-slate-700">{rupees(pr.franchise.total)}</span>
            </div>
          )}
          <div className="flex justify-between items-baseline border-l-2 border-[#C9A84C] pl-2 -ml-2">
            <span className="text-[11px] uppercase tracking-wider text-[#C9A84C]">Customer MRP</span>
            <span className="font-sans font-semibold text-xl text-[#0D1B2A] tabular-nums">{rupees(pr.customer.total)}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

export default function FranchisePortal() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<CatalogueView />} />
        <Route path="search" element={<SearchView />} />
        <Route path="reports" element={<ReportsView />} />
      </Route>
    </Routes>
  );
}
